'use no memo';
/**
 * SDK Loading Watchdog Hook
 *
 * AIDEV-NOTE: Issue #567 follow-up. The Logto SDK can enter an internal retry
 * loop on `/oidc/token` when the stored refresh token is invalid
 * (corrupted/expired/revoked) at app boot. In that state `logtoIsLoading`
 * stays `true` indefinitely and every other auth effect in this provider
 * early-returns on `logtoIsLoading`, so the user sees the loading screen
 * forever while the network tab fills with 400 `invalid_grant` responses.
 *
 * This watchdog observes the SDK from outside its loading state. When the
 * SDK stays "loading" for longer than `timeoutMs` AND the user had a valid
 * session last page load (`wasAuthenticated`), we force-terminate: flip
 * `SESSION_EXPIRED`, open the drawer, and clear the cached auth snapshot.
 *
 * The watchdog is self-disarming: `clearCachedAuth()` flips `wasAuthenticated`
 * to `false`, so a subsequent legitimate loading cycle (e.g. after the user
 * clicks "Sign in again") never re-arms it.
 */

import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { setGlobalSessionExpired } from '@api-client/api-client';
import { logger } from '@utils/logger';
import { clearAuthState } from './clearAuthState';
import { AuthSessionState, type AuthAction } from './authTypes';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * How long to wait before deciding the SDK is stuck.
 *
 * The initial token exchange on a healthy network completes well under a
 * second; five seconds is generous enough to avoid false positives on slow
 * networks while still rescuing the user from an indefinite load.
 */
export const SDK_STUCK_TIMEOUT_MS = 5_000;

// =============================================================================
// HOOK OPTIONS
// =============================================================================

export interface UseSdkLoadingWatchdogOptions {
  /** Current Logto SDK loading state (true while the SDK is initializing). */
  logtoIsLoading: boolean;
  /** Whether the user had a successfully-authenticated session previously. */
  wasAuthenticated: boolean;
  /** Ref to current session state for synchronous reads. */
  sessionStateRef: React.RefObject<AuthSessionState>;
  /** Dispatch function for session state updates. */
  dispatch: React.Dispatch<AuthAction>;
  /** Store action that clears cached user snapshot (flips wasAuthenticated to false). */
  clearCachedAuth: () => void;
  /** Opens the SessionExpiredDrawer. */
  openSessionExpiredDrawer: () => void;
  /** React Query client for cancel+clear inside the shared cleanup. */
  queryClient: QueryClient;
  /** Zustand setter for auth flag. */
  setAuthenticated: (v: boolean) => void;
  /** Zustand setter for current user snapshot. */
  setCurrentUser: (u: null) => void;
  /** Evicts the cached current-user React Query entry. */
  clearUser: () => void;
  /** Logto SDK clearAllTokens function. Best-effort hint to stop the retry loop. */
  clearAllTokens: () => Promise<void>;
  /** Override for tests. Defaults to SDK_STUCK_TIMEOUT_MS (5s). */
  timeoutMs?: number;
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

export function useSdkLoadingWatchdog({
  logtoIsLoading,
  wasAuthenticated,
  sessionStateRef,
  dispatch,
  clearCachedAuth,
  openSessionExpiredDrawer,
  queryClient,
  setAuthenticated,
  setCurrentUser,
  clearUser,
  clearAllTokens,
  timeoutMs = SDK_STUCK_TIMEOUT_MS,
}: UseSdkLoadingWatchdogOptions): void {
  useEffect(() => {
    if (!logtoIsLoading) return;
    if (!wasAuthenticated) return;
    if (sessionStateRef.current === AuthSessionState.SESSION_EXPIRED) return;
    // Do not fire while we're already in an active recovery flow. Native
    // re-auth via handleInvalidGrant sets REAUTHENTICATING and then awaits a
    // system-browser round-trip. That round-trip keeps logtoIsLoading=true and
    // can legitimately exceed 5s (MFA, slow networks, user reading the page).
    // Without this gate the watchdog would fire mid-OAuth, run
    // clearAuthState(), and yank tokens / PKCE state out from under the
    // in-flight sign-in — causing the deep-link callback to fail even though
    // auth actually succeeded.
    if (sessionStateRef.current === AuthSessionState.REAUTHENTICATING) return;

    const timer = setTimeout(() => {
      logger.warn(`Logto SDK stuck in loading state for >${timeoutMs}ms - forcing SESSION_EXPIRED`);
      setGlobalSessionExpired(true);
      sessionStateRef.current = AuthSessionState.SESSION_EXPIRED;
      dispatch({ type: 'SET_SESSION_EXPIRED' });
      // `clearCachedAuth` flips `wasAuthenticated` to false so this watchdog
      // self-disarms. Keep it in front of `clearAuthState` because
      // `clearAuthState` doesn't touch the Zustand watchdog-arming fields.
      clearCachedAuth();
      openSessionExpiredDrawer();
      // Issue #567 follow-up: the earlier version only fired a best-effort
      // `clearAllTokens()` here, leaving the cached `user`, `isAuthenticated`,
      // and the `currentUserKey()` React Query entry intact. That let stale
      // authenticated data leak into guest mode after the drawer closed.
      // `clearAuthState` handles the full cleanup (cancelQueries → clear →
      // flip Zustand → clearAllTokens) with the same best-effort semantics
      // for token-clearing failures.
      void clearAuthState({
        queryClient,
        setAuthenticated,
        setCurrentUser,
        clearUser,
        clearAllTokens,
      }).catch((err: unknown) => {
        logger.error(
          'clearAuthState after watchdog threw - stale authenticated data may persist:',
          err,
        );
      });
    }, timeoutMs);

    return () => {
      clearTimeout(timer);
    };
  }, [
    logtoIsLoading,
    wasAuthenticated,
    sessionStateRef,
    dispatch,
    clearCachedAuth,
    openSessionExpiredDrawer,
    queryClient,
    setAuthenticated,
    setCurrentUser,
    clearUser,
    clearAllTokens,
    timeoutMs,
  ]);
}
